import React, { useMemo, useCallback, useEffect } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  VEHICLE_TASK_CATEGORY_OPTIONS,
  VehicleTaskOption,
} from "@/config";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export type VehicleTaskCategoryValue = string;

export interface CategorySelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected category value */
  value?: VehicleTaskCategoryValue | null | undefined;

  /** Callback fired when category selection changes: (name, value) */
  onChange?: (name: string, value: VehicleTaskCategoryValue | null) => void;

  /**
   * Whether the field is required.
   * When true: auto-selects first option if value is null, user cannot deselect.
   * When false: clicking the selected chip deselects it.
   */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the input wrapper (for label/error consistency) */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Optional label for the field */
  label?: string;

  /** Chip variant: outline (default) or filled */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Styles
// ============================================

/**
 * Chip label styles to visually match Mantine md input height.
 * Each chip stretches to fill its flex cell via width: 100%.
 */
const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

/**
 * Each chip root takes equal space in the flex row.
 * min-width ensures chips wrap into 3+2 rows on narrow screens
 * rather than squeezing all 5 onto one truncated line.
 * ~30% guarantees at most 3 chips per row (accounting for gaps).
 */
const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: "30%",
};

/** Flex container: full-width row that wraps on small screens */
const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Constants
// ============================================

const DEFAULT_VALUE = VEHICLE_TASK_CATEGORY_OPTIONS[0]?.value as VehicleTaskCategoryValue;

// ============================================
// Component
// ============================================

const CategorySelectorComponent: React.FC<CategorySelectorProps> = ({
  name = "category",
  value,
  onChange,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  variant = "outline",
}) => {
  const { t } = useTranslation(["vehicleTasks"]);

  // Auto-select first option when required and value is null/undefined
  useEffect(() => {
    if (required && value == null && onChange) {
      onChange(name, DEFAULT_VALUE);
    }
  }, [required, value, name, onChange]);

  // Handle individual chip toggle with single-select semantics.
  // Each Chip is a controlled checkbox, so onChange fires with
  // checked=true (selecting) or checked=false (deselecting).
  const handleChipChange = useCallback(
    (chipValue: string, checked: boolean) => {
      if (checked) {
        onChange?.(name, chipValue as VehicleTaskCategoryValue);
      } else {
        if (!required) {
          onChange?.(name, null);
        }
      }
    },
    [name, required, onChange]
  );

  // Build individually controlled chip elements (no Chip.Group)
  const chips = useMemo(() => {
    return VEHICLE_TASK_CATEGORY_OPTIONS.map((config: VehicleTaskOption) => {
      const chipValue = String(config.value);
      const isChecked = chipValue === value;
      const translatedLabel = t(`vehicleTasks:${config.labelKey}`, {
        defaultValue: chipValue,
      });

      return (
        <Chip
          key={chipValue}
          checked={isChecked}
          onChange={(checked) => handleChipChange(chipValue, checked)}
          color={config.color}
          variant={variant}
          size="sm"
          disabled={disabled}
          icon={
            config.icon ? (
              <AppIcon iconName={config.icon} size={20} />
            ) : undefined
          }
          styles={{
            label: CHIP_LABEL_STYLES,
            iconWrapper: CHIP_ICON_WRAPPER_STYLES,
            root: CHIP_ROOT_STYLES,
          }}
        >
          {translatedLabel}
        </Chip>
      );
    });
  }, [value, t, variant, disabled, handleChipChange]);

  return (
    <Input.Wrapper
      label={label}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={label ? 4 : 0} style={GROUP_STYLES}>
        {chips}
      </Box>
    </Input.Wrapper>
  );
};

export const CategorySelector = React.memo(CategorySelectorComponent);